import {
	GQLOrderItemFragment,
	GQLOrderItemPrintDecorationType,
	GQLOrderItemPrintSideType,
	GQLOrderItemTileFragment,
	GQLProductSideAvailableDecorationType,
	GQLProductSideSideType,
} from '@theprintbar/graphql';
import { capitalise, mapEnumValue } from '../../utils';
import { MerchFormServer } from '../merch';

export const orderItemHasDTG = (orderItem?: GQLOrderItemTileFragment | null) =>
	!!orderItem?.prints?.some(d => d.decoration === GQLOrderItemPrintDecorationType.Dtg);
export const orderItemHasDTF = (orderItem?: { prints?: { decoration: GQLOrderItemPrintDecorationType }[] | null } | null) =>
	!!orderItem?.prints?.some(d => d.decoration === GQLOrderItemPrintDecorationType.Dtf);
export const orderItemHasSCR = (orderItem?: GQLOrderItemTileFragment | null, ignoreCancelled?: boolean) =>
	!!orderItem?.prints?.some(d => d.decoration === GQLOrderItemPrintDecorationType.Scr) && (!ignoreCancelled || !orderItem.cancelled);
export const orderItemHasEMB = (orderItem?: GQLOrderItemTileFragment | null) =>
	!!orderItem?.prints?.some(d => d.decoration === GQLOrderItemPrintDecorationType.Emb);
export const orderItemIsTrends = (orderItem?: GQLOrderItemTileFragment | null, ignoreCancelled?: boolean) =>
	orderItem?.product?.code.includes('TRENDS') && !orderItemIs3PL(orderItem) && (!ignoreCancelled || !orderItem.cancelled);
export const orderItemIs3PL = (orderItem?: GQLOrderItemTileFragment | null) =>
	orderItem?.sizes?.some(s => !!s.storeSuppliedVariant?.warehouseSku);

export const orderItemDTGCount = (orderItem?: GQLOrderItemTileFragment | null) =>
	orderItem?.prints?.filter(d => d.decoration === GQLOrderItemPrintDecorationType.Dtg).length || 0;
export const orderItemDTFCount = (orderItem?: GQLOrderItemTileFragment | null) =>
	orderItem?.prints?.filter(d => d.decoration === GQLOrderItemPrintDecorationType.Dtf).length || 0;
export const orderItemSCRCount = (orderItem?: GQLOrderItemTileFragment | null, ignoreCancelled?: boolean) =>
	orderItem?.prints?.filter(d => d.decoration === GQLOrderItemPrintDecorationType.Scr && (!ignoreCancelled || !orderItem.cancelled))
		.length || 0;
export const orderItemEMBCount = (orderItem?: GQLOrderItemTileFragment | null) =>
	orderItem?.prints?.filter(d => d.decoration === GQLOrderItemPrintDecorationType.Emb).length || 0;

export const orderItemDTGDecorations = (orderItem?: GQLOrderItemTileFragment | null) =>
	orderItem?.prints?.filter(d => d.decoration === GQLOrderItemPrintDecorationType.Dtg) || [];
export const orderItemDTFDecorations = (orderItem?: GQLOrderItemTileFragment | null) =>
	orderItem?.prints?.filter(d => d.decoration === GQLOrderItemPrintDecorationType.Dtf) || [];
export const orderItemSCRDecorations = (orderItem?: GQLOrderItemTileFragment | null, ignoreCancelled?: boolean) =>
	orderItem?.prints?.filter(d => d.decoration === GQLOrderItemPrintDecorationType.Scr && (!ignoreCancelled || !orderItem.cancelled)) ||
	[];
export const orderItemEMBDecorations = (orderItem?: GQLOrderItemTileFragment | null) =>
	orderItem?.prints?.filter(d => d.decoration === GQLOrderItemPrintDecorationType.Emb) || [];

//SCR and Trends items are decorated by outsourced partners rather than at the warehouse, so they never make it
//onto the Fulfill Engine order - the rest of the order still does, which splits the shipment
export const orderItemIsExternallyFulfilled = (orderItem?: GQLOrderItemTileFragment | null, ignoreCancelled?: boolean) =>
	!orderItemIs3PL(orderItem) && (orderItemHasSCR(orderItem, ignoreCancelled) || !!orderItemIsTrends(orderItem, ignoreCancelled));
export const orderItemGoesToWarehouse = (orderItem?: GQLOrderItemTileFragment | null) =>
	!!orderItem && !orderItem.cancelled && !orderItemIsExternallyFulfilled(orderItem);
export const orderHasWarehouseItems = (orderItems?: GQLOrderItemTileFragment[] | null) => !!orderItems?.some(orderItemGoesToWarehouse);
export const orderHasExternalItems = (orderItems?: GQLOrderItemTileFragment[] | null, ignoreCancelled?: boolean) =>
	!!orderItems?.some(orderItem => orderItemIsExternallyFulfilled(orderItem, ignoreCancelled));

export const orderHasDTG = (orderItems?: GQLOrderItemTileFragment[] | null) => !!orderItems?.some(orderItemHasDTG);
export const orderHasDTF = (orderItems?: GQLOrderItemTileFragment[] | null) => !!orderItems?.some(orderItemHasDTF);
export const orderHasSCR = (orderItems?: GQLOrderItemTileFragment[] | null, ignoreCancelled?: boolean) =>
	!!orderItems?.some(orderItem => orderItemHasSCR(orderItem, ignoreCancelled));
export const orderHasEMB = (orderItems?: GQLOrderItemTileFragment[] | null) => !!orderItems?.some(orderItemHasEMB);
export const orderHasTrends = (orderItems?: GQLOrderItemTileFragment[] | null, ignoreCancelled?: boolean) =>
	!!orderItems?.some(orderItem => orderItemIsTrends(orderItem, ignoreCancelled));

export function getOrderItemChanges(item: GQLOrderItemTileFragment, merch: MerchFormServer) {
	const changes: string[] = [];
	try {
		if (item.colour?.colour.code !== merch.colour) changes.push(`Colour changed from ${item.colour?.colour.code} to ${merch.colour}`);
		//Compare the sizes and quantities
		const newSizes = merch.sizing.filter(s => !item.sizes?.some(os => os.size?.code === s.size));
		if (newSizes.length > 0) {
			newSizes.forEach(s => {
				changes.push(`Added ${s.quantity}x ${s.size}`);
			});
		}

		const removedSizes = item.sizes?.filter(os => !merch.sizing.some(s => s.size === os.size?.code));
		if (removedSizes && removedSizes.length > 0) {
			removedSizes.forEach(s => {
				changes.push(`Removed ${s.quantity}x ${s.size?.code}`);
			});
		}

		const changedSizes = merch.sizing
			.filter(s => item.sizes?.some(os => os.size?.code === s.size && os.quantity !== s.quantity))
			.map(s => {
				const oldSizeQuantity = item.sizes?.find(os => os.size?.code === s.size)?.quantity || 0;
				return oldSizeQuantity > s.quantity
					? `Removed ${s.quantity - oldSizeQuantity}x ${s.size}`
					: `Added ${oldSizeQuantity - s.quantity}x ${s.size}`;
			});

		changes.push(...changedSizes);

		const newDecorations = merch.decorations.filter(
			d => !item.prints?.some(op => !!op.side && mapEnumValue(GQLOrderItemPrintSideType, GQLProductSideSideType, op.side) === d.side),
		);
		if (newDecorations.length > 0) {
			newDecorations.forEach(d => {
				changes.push(`Added ${capitalise(d.side)} - ${d.type.toUpperCase()}`);
			});
		}

		const removedDecorations = item.prints?.filter(
			op =>
				!merch.decorations.some(
					d => !!d.side && mapEnumValue(GQLOrderItemPrintSideType, GQLProductSideSideType, op.side) === d.side,
				),
		);
		if (removedDecorations && removedDecorations.length > 0) {
			removedDecorations.forEach(d => {
				changes.push(`Removed ${capitalise(d.side)}`);
			});
		}

		const changedDecorations = merch.decorations.filter(d =>
			item.prints?.some(
				op =>
					!!op.side &&
					mapEnumValue(GQLOrderItemPrintSideType, GQLProductSideSideType, op.side) === d.side &&
					mapEnumValue(GQLOrderItemPrintDecorationType, GQLProductSideAvailableDecorationType, op.decoration) !== d.type,
			),
		);
		if (changedDecorations.length > 0) {
			changedDecorations.forEach(d => {
				const oldDecoration = item.prints?.find(
					op => !!op.side && mapEnumValue(GQLOrderItemPrintSideType, GQLProductSideSideType, op.side) === d.side,
				);
				changes.push(`${capitalise(d.side)} - ${oldDecoration?.decoration} -> ${d.type}`);
			});
		}
	} catch (e) {
		console.error('Error getting order item changes:', e);
	}

	return changes;
}
